/*
 | =========================================================
 | This File is for member hub vaults state getters handling
 | =========================================================
 */
import dayjs from 'dayjs'
import memberhubConstants from '~/constants/memberhub.constants'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import customParseFormat from 'dayjs/plugin/customParseFormat.js'
import { upcomingRateRise } from '~/helpers/functions'
import advancedFormat from 'dayjs/plugin/advancedFormat'

dayjs.extend(advancedFormat)
dayjs.extend(isSameOrBefore)
dayjs.extend(customParseFormat)

/*
 | =====================================
 | Premium amount rendering per vertical
 | @param product
 | @returns {null|*|string}
 | =====================================
 */
const premiumAmountPerVertical = product => {
  switch (product.vertical) {
    case memberhubConstants.VERTICAL_ID.HEALTH_INSURANCE:
      if(product.is_purchase_history && product.rate_rise.current_premium){
        return formatCurrency(product.rate_rise.current_premium)
      }
      return product.payload?.premium_amount ? formatCurrency(product.payload?.premium_amount) : (product.premium_amount ? formatCurrency(product.premium_amount) : '')
    case memberhubConstants.VERTICAL_ID.LIFE_INSURANCE:
      if (product.is_purchase_history && product.rate_rise?.current_premium) {
        return formatCurrency(product.rate_rise.current_premium)
      }
      return product.is_purchase_history ? formatCurrency(product.payload?.premium) : formatCurrency(product.payload?.premium_amount)
    case memberhubConstants.VERTICAL_ID.ENERGY: {
      if (product.is_purchase_history && product.rate_rise?.current_premium) {
        return formatCurrency(product.rate_rise.current_premium)
      }
      if (product.is_purchase_history) {
        const elec = parseFloat(product.payload?.electricity_plan_bill_period_total)
        const gas = parseFloat(product.payload?.gas_plan_bill_period_total)
        const isGas = product.payload?.energy_product_type === memberhubConstants.ENERGY_PRODUCT_TYPE_FLAG.GAS
        const [primary, secondary] = isGas ? [gas, elec] : [elec, gas]
        return primary > 0 ? formatCurrency(primary) : (secondary > 0 ? formatCurrency(secondary) : '')
      }
      // total_bill_cost is the actual billed amount. CDR's structured Adatree data is
      // trusted to show it precisely; n8n bill-upload vaults can also carry this field,
      // but it's OCR-extracted and not guaranteed accurate, so those stay on the
      // bucketed monthly_bill range ("$300-$449") even when total_bill_cost is present.
      // See CurrentPolicySection.vue.
      if (isCdrConnectedProduct(product)) {
        const actualBillAmount = parseFloat(product.payload?.total_bill_cost)
        if (!isNaN(actualBillAmount) && actualBillAmount > 0) {
          return formatCurrency(actualBillAmount)
        }
      }
      return product.payload?.monthly_bill
    }
    case memberhubConstants.VERTICAL_ID.CAR_INSURANCE:
      return product.payload?.payment_amount ? formatCurrency(product.payload?.payment_amount) : ''
    case memberhubConstants.VERTICAL_ID.GENERIC:
      if (product.payload?.generic_product_type === memberhubConstants.GENERIC_PRODUCT_TYPE_FLAG.BROADBAND) {
        return ''
      }
      if (product.payload?.generic_product_type === memberhubConstants.GENERIC_PRODUCT_TYPE_FLAG.SIM) {
        return ''
      }
      if (product.is_purchase_history && product.payload.generic_product_type) {
        return product.payload?.price ? formatCurrency(product.payload?.price) : ''
      } return product.payload?.payment_amount ? formatCurrency(product.payload?.payment_amount) : ''
    case memberhubConstants.VERTICAL_ID.HOME_LOANS:
      // A settled loan carries no repayment amount, so the card leads with the
      // loan amount instead; the term copy that follows it is the card's own.
      if (product.is_purchase_history) {
        return product.payload?.loan_amount ? formatCurrency(product.payload?.loan_amount) : ''
      }
      return product.payload?.last_payment_amount ? formatCurrency(product.payload?.last_payment_amount) : ''
    default:
      return formatCurrency(product.payload?.premium_amount)
  }
}

/*
   | =========================
   | Product name per vertical
   | @param product
   | @returns {null|*|string}
   | =========================
   */
const productNamePerVertical = product => {
  switch (product.vertical) {
    case memberhubConstants.VERTICAL_ID.HEALTH_INSURANCE:
      return product.payload?.policy_details?.derived_name || product.payload?.policy_details?.name
    case memberhubConstants.VERTICAL_ID.LIFE_INSURANCE:
      if (product.is_purchase_history) {
        return product.payload?.quote_name || (product.payload?.proceeding_insurer ? `${product.payload?.proceeding_insurer} Protection` : 'Your Life Insurance Policy')
      }
      return product.payload?.provider?.fund_name
    case memberhubConstants.VERTICAL_ID.HOME_LOANS:
      if (product.is_purchase_history) {
        return product.payload?.loan_product || lenderName(product.payload?.lender)
      }
      return lenderName(product.payload?.lender)
    case memberhubConstants.VERTICAL_ID.ENERGY:
      return product.payload?.product_type
    case memberhubConstants.VERTICAL_ID.CAR_INSURANCE:
      return product.payload?.provider
    case memberhubConstants.VERTICAL_ID.GENERIC:
      return product.payload?.provider || (product.payload?.category === 'Other' ? product.payload?.other_category : product.payload?.category)
    default:
      return product.payload?.policy_details?.name
  }
}

/*
    | =========================
    | Payment frequency per vertical
    | @param product
    | @returns {null|*|string}
    | =========================
    */
const paymentFrequencyPerVertical = product => {
  switch (product.vertical) {
    case memberhubConstants.VERTICAL_ID.HOME_LOANS:
      return `${paymentFrequencyText(product.payload?.payment_frequency)}**`
    case memberhubConstants.VERTICAL_ID.ENERGY:
      return `${paymentFrequencyText(product.rate_rise?.current_premium ? (product.rate_rise?.new_frequency || product.payload?.payment_frequency) : product.payload?.payment_frequency)}*`
    case memberhubConstants.VERTICAL_ID.CAR_INSURANCE:
      return paymentFrequencyText(product.payload?.payment_frequency || product.payment_frequency)
    case memberhubConstants.VERTICAL_ID.GENERIC:
      if (product.is_purchase_history && product.payload.generic_product_type) {
        return 'Month'
      }
      return paymentFrequencyText(product.payload?.payment_frequency || product.payment_frequency)
    default:
      return paymentFrequencyText(product.rate_rise?.current_premium?(product.rate_rise?.new_frequency || product.payload?.payment_frequency || product.payment_frequency) : (product.payload?.payment_frequency || product.payment_frequency))
  }
}

/*
    | =========================
    | Payment frequency text
    | @param paymentFrequency
    | @returns {null|*|string}
    | =========================
    */
const paymentFrequencyText = paymentFrequency => {
  return getKey(paymentFrequency, {
    Weekly:
       'Week',
    Fortnightly:
       'Fortnight',
    Monthly:
       'Month',
    Quarterly:
       'Quarter',
    Annual:
       'Year',
    Annually:
       'Year',
    Yearly:
       'Year',
    'Bi-Monthly':
       'Bi-Month',
    'Half Yearly':
       'Half Year',
    'Half-yearly':
       'Half Year',
    'HalfYearly':
       'Half Year',
    default:
       'Month'
  })
}

/*
   | =============================
   | Product rate string rendering
   | @param product
   | @returns string
   | =============================
   */
const productInterestRateString = product => {
  // eslint-disable-next-line default-case
  switch (product.payload?.loan_type) {
    case 'Fixed':
      return product.payload?.fixed_rate ? `${appendPercentage(product.payload?.fixed_rate)} fixed${(product.payload?.fixed_rate_term && product.payload?.fixed_rate_term !== 'Other' ? ` for ${product.payload?.fixed_rate_term}` : '')}` : ''
    case 'Variable':
      return product.payload?.variable_rate ? `${appendPercentage(product.payload?.variable_rate)} variable` : ''
    case 'Split (Combined)':
      return product.payload?.fixed_rate || product.payload?.variable_rate ? `${appendPercentage(product.payload?.fixed_rate)} fixed${(product.payload?.fixed_rate_term && product.payload?.fixed_rate_term !== 'Other' ? ` for ${product.payload?.fixed_rate_term}` : '')}, ${appendPercentage(product.payload?.variable_rate)} variable` : ''
  }
}

/*
     | ===============================
     | Home loan amount text rendering
     | @param product
     | @returns {`${string}`}
     | ===============================
     */
const homeLoanAmount = product => {
  const amountSplitUps = []
  const fixedAmount = product.payload?.fixed_loan_amount ? `${formatCurrency(product.payload?.fixed_loan_amount)} fixed` : null
  const variableAmount = product.payload?.variable_loan_amount ? `${formatCurrency(product.payload?.variable_loan_amount)} variable` : null
  if (fixedAmount) {
    amountSplitUps.push(fixedAmount)
  }
  if (variableAmount) {
    amountSplitUps.push(variableAmount)
  }
  const loanTermSuffix = product.payload?.loan_term && product.payload?.loan_term !== 'Other' ? `over ${product.payload?.loan_term}` : ''
  switch (product.payload?.loan_type) {
    case 'Split (Combined)':
      return `${formatCurrency(product.payload?.loan_amount)} ${loanTermSuffix} ${(amountSplitUps.length ? `(${amountSplitUps.join(' + ')})` : '')}`
    default:
      return `${formatCurrency(product.payload?.loan_amount)} ${loanTermSuffix}`
  }
}

/*
 * Get LIC application status from object
 * @param status
 * @returns {*}
 */
const licStatus = status => {
  return getKey(status, {
    Submitted: { text: 'Submitted', color: 'blueb' },
    Assessed: { text: 'Assessed', color: 'yellowb' },
    Active: { text: 'Active', color: 'successbd' },
    Declined: { text: 'Declined', color: 'errorbd' },
    'Refer to Broker': { text: 'Refer to broker', color: 'purplebd' },
    'Claim Made': { text: 'Claim made', color: 'orangebd' },
    Cancelled: { text: 'Cancelled', color: 'bordered' },
    Closed: { text: 'Closed', color: 'bordered' }
  })
}

/*
 * Derive health insurance policy type from product payload
 * Possible values: 'combined' | 'hospital' | 'extras' | null (non-health verticals)
 *
 * Priority order:
 *  1. Explicit cover_type field on the payload
 *  2. extra_cover_exists flag on the payload
 *  3. product_type on policy_details + cover availability
 *  4. Fallback: presence of hospital_covers vs extra_covers data
 *
 * @param {Object} product
 * @returns {'combined'|'hospital'|'extras'|null}
 */
const getHealthPolicyType = product => {
  if (product?.vertical !== memberhubConstants.VERTICAL_ID.HEALTH_INSURANCE) return null

  const payload = product?.payload || {}
  const coverType = payload?.cover_type

  // 1. Explicit cover_type field (set by funnel / vault form)
  if (coverType === 'Combined') return memberhubConstants.HEALTH_POLICY_TYPE.COMBINED
  if (coverType === 'Hospital') return memberhubConstants.HEALTH_POLICY_TYPE.HOSPITAL
  if (coverType === 'Extras' || coverType === 'GeneralHealth') return memberhubConstants.HEALTH_POLICY_TYPE.EXTRAS

  // 2. extra_cover_exists flag (set when purchased policy has linked extras product)
  if (payload?.extra_cover_exists) return memberhubConstants.HEALTH_POLICY_TYPE.COMBINED

  // 3. product_type on policy_details (purchase history records)
  //    product_type === 'Hospital' means it is the hospital leg of a potentially combined policy;
  //    if no extras data is present it is hospital-only.
  const policyProductType = payload?.policy_details?.product_type
  if (policyProductType === 'Hospital') {
    const hasExtraCovers = !!(
      payload?.extra_details?.extra_covers?.length ||
      payload?.policy_details?.extra_covers?.length
    )
    return hasExtraCovers ? memberhubConstants.HEALTH_POLICY_TYPE.COMBINED : memberhubConstants.HEALTH_POLICY_TYPE.HOSPITAL
  }
  if (policyProductType === 'GeneralHealth' || policyProductType === 'Extras') return memberhubConstants.HEALTH_POLICY_TYPE.EXTRAS

  // 4. Fallback: infer from data availability
  const hasHospitalCovers = !!(payload?.policy_details?.hospital_covers?.length)
  const hasExtraCovers = !!(
    payload?.extra_details?.extra_covers?.length ||
    payload?.policy_details?.extra_covers?.length
  )
  if (hasHospitalCovers && hasExtraCovers) return memberhubConstants.HEALTH_POLICY_TYPE.COMBINED
  if (hasHospitalCovers) return memberhubConstants.HEALTH_POLICY_TYPE.HOSPITAL
  if (hasExtraCovers) return memberhubConstants.HEALTH_POLICY_TYPE.EXTRAS

  return null
}

const getPurchaseDate = product => {
  return getKey(product.vertical,{
    [memberhubConstants.VERTICAL_ID.HEALTH_INSURANCE]: dayjs(product.payload?.purchase_date,'DD/MM/YYYY'),
    [memberhubConstants.VERTICAL_ID.LIFE_INSURANCE]: dayjs(product.payload?.app_submit_date),
    [memberhubConstants.VERTICAL_ID.ENERGY]: dayjs(product.payload?.application_date, 'DD/MM/YYYY'),
    [memberhubConstants.VERTICAL_ID.HOME_LOANS]: dayjs(product.payload?.loan_start_date, 'DD/MM/YYYY'),
    [memberhubConstants.VERTICAL_ID.CAR_INSURANCE]: dayjs(product.payload?.application_date, 'DD/MM/YYYY'),
    [memberhubConstants.VERTICAL_ID.GENERIC]: dayjs(product.payload?.application_date, 'DD/MM/YYYY')
  })
}

const providerNamePerVertical = product =>
  product.payload?.provider?.fund_name
  || product.payload?.provider?.name
  // Home loans send the lender as either an object or a plain name string
  || lenderName(product.payload?.lender)
  || product.payload?.retailer?.fund_name
  || product.payload?.retailer
  || product.payload?.proceeding_insurer
  || product.payload?.provider

const productAdditionalDetails = product => {
  // Neither shape carries every funnel field below — a purchased loan's rate
  // lives on `rate_structure`/`interest_rate` and `loan_type` means occupancy;
  // a self-entered loan's single-page form no longer asks loan type, started
  // on or payment type at all. Source them from the shared mapper so the card
  // and details page cannot disagree.
  const homeLoanDetails = product.is_purchase_history
    ? [
      {
        title: 'Rate',
        description: homeLoanFieldValue(product, 'rate')
      },
      {
        title: 'Repayment Type',
        description: homeLoanFieldValue(product, 'repaymentType')
      },
      {
        title: 'Property Use',
        description: homeLoanFieldValue(product, 'propertyUse')
      }
    ]
    : [
      {
        title: 'Current Interest Rate',
        description: homeLoanFieldValue(product, 'rate')
      },
      {
        title: 'Property Type',
        description: homeLoanFieldValue(product, 'propertyType')
      }
    ]

  return getKey(product.vertical,{
    [memberhubConstants.VERTICAL_ID.LIFE_INSURANCE]: [
      {
        title: 'Application Status',
        description: licStatus(product?.payload?.status)?.text || ''
      },
      {
        title: 'Last Updated',
        description: formatDate(product.payload?.last_updated_at,'YYYY-MM-DD','DD MMM YYYY') || ''
      }
    ],
    [memberhubConstants.VERTICAL_ID.HOME_LOANS]: homeLoanDetails,
    [memberhubConstants.VERTICAL_ID.CAR_INSURANCE]: [
      {
        title: 'Reference Number',
        description: product.payload?.reference || ''
      },
      {
        title: 'Next Payment Date',
        description: formatDate(product.payload?.actual_next_payment_date,'DD/MM/YYYY','DD MMM YYYY') || ''
      },
      {
        title: 'Renewal/Expiry Date',
        description: formatDate(product.payload?.renewal_date,'DD/MM/YYYY','DD MMM YYYY') || ''
      }
    ],
    [memberhubConstants.VERTICAL_ID.GENERIC]: [
      {
        title: 'Reference Number',
        description: product.payload?.reference || ''
      },
      {
        title: 'Next Payment Date',
        description: formatDate(product.payload?.actual_next_payment_date,'DD/MM/YYYY','DD MMM YYYY') || ''
      },
      {
        title: 'Renewal/Expiry Date',
        description: formatDate(product.payload?.renewal_date,'DD/MM/YYYY','DD MMM YYYY') || ''
      }
    ],
    default: [
      {
        title: 'Reference Number',
        description: product.payload?.reference || ''
      },
      {
        title: 'Next Payment Date',
        description: formatDate(product.payload?.actual_next_payment_date,'DD/MM/YYYY','DD MMM YYYY') || ''
      },
      {
        title: 'Coverage',
        description: product.payload?.coverage === 'Single Parent' ? 'Single Parent Family' : (product.payload?.coverage || '')
      }
    ]
  })
}

/*
 * Check if product is a life insurance purchase
 * @param {Object} product
 * @returns {boolean}
 */
const checkLifePurchase = product => {
  return product?.is_purchase_history &&
      product?.vertical === memberhubConstants.VERTICAL_ID.LIFE_INSURANCE
}

/*
 * Check if life insurance purchase is active (in-force date has passed)
 * @param {Object} state
 * @returns {boolean}
 */
const checkActiveLifePurchase = product => {
  const inForceDate = product?.payload?.in_force_date

  // Call the other getter through 'this'
  if (!checkLifePurchase(product) || !inForceDate) return false

  return dayjs().diff(dayjs(inForceDate, 'YYYY-MM-DD'), 'day') >= 0
}

/*
 * Check if an active life insurance purchase is still within the post-sale
 * Quick Compare lock window (Get a Quote is hidden for this many days after in-force)
 * @param {Object} product
 * @param {number} days
 * @returns {boolean}
 */
const checkLifeQuickCompareLocked = (product, days = 90) => {
  return checkActiveLifePurchase(product) &&
    dayjs().diff(dayjs(product?.payload?.in_force_date, 'YYYY-MM-DD'), 'day') < days
}

/*
     * Determines the date a product was added to the vault.
     *
     * This function calculates the date based on the product's purchase history and other relevant dates.
     * - If the product is a life insurance purchase and is not active, it uses the application submission date.
     * - If the product has a purchase date, it uses that.
     * - If the product has a close date, it uses that.
     * - If the product has an application date, it uses that.
     * - Otherwise, it defaults to the product's creation date.
     *
     * Energy and generic/telco store the Salesforce close_date under application_date, so without
     * that step they fall through to created_at - the vault ROW timestamp, which moves forward every
     * time the synced payload changes. See CLUB-756.
     *
     * Deliberately NOT getPurchaseDate(): that answers "when was this purchased" and maps life to
     * app_submit_date unconditionally and home loans to loan_start_date, which would change what
     * those two cards display today.
     *
     * @param {Object} product - The product object containing purchase history and date information.
     * @param {boolean} product.is_purchase_history - Indicates if the product is part of the purchase history.
     * @param {Object} product.payload - Contains various date fields related to the product.
     * @param {string} [product.payload.app_submit_date] - The application submission date (YYYY-MM-DD).
     * @param {string} [product.payload.purchase_date] - The purchase date (DD/MM/YYYY).
     * @param {string} [product.payload.close_date] - The close date (DD/MM/YYYY).
     * @param {string} [product.payload.application_date] - The application date (DD/MM/YYYY) - energy, generic/telco.
     * @param {string} product.created_at - The creation date of the product (YYYY-MM-DD).
     * @returns {string} - The formatted date the product was added to the vault (DD MMM YYYY).
     */
const dateAddedToVault = product => {
  if (product?.is_purchase_history) {
    if (checkLifePurchase(product) && !checkActiveLifePurchase(product)) {
      return formatDate(product?.payload?.app_submit_date, 'YYYY-MM-DD', 'DD MMM YYYY')
    }
    if (product?.payload?.purchase_date) {
      return formatDate(product?.payload?.purchase_date, 'DD/MM/YYYY', 'DD MMM YYYY')
    }
    if (product?.payload?.close_date) {
      return formatDate(product?.payload?.close_date, 'YYYY-MM-DD', 'DD MMM YYYY')
    }
    // Energy / generic-telco: the SF close_date lands here, in DD/MM/YYYY.
    // formatDate has no validity check, so guard rather than render 'Invalid Date'.
    if (dayjs(product?.payload?.application_date, 'DD/MM/YYYY', true).isValid()) {
      return formatDate(product?.payload?.application_date, 'DD/MM/YYYY', 'DD MMM YYYY')
    }
    return formatDate(product.created_at, 'YYYY-MM-DD', 'DD MMM YYYY')
  }
  return formatDate(product.created_at, 'YYYY-MM-DD', 'DD MMM YYYY')
}

/*
 * Get rate rise configuration for a product
 * @param {Object} product - The product object
 * @returns {Object} - Rate rise configuration object
 */
const getRateRiseConfig = product => {
  // Get upcoming rate rise date configuration
  const upcomingRateRiseDate = upcomingRateRise(product?.payload?.provider)
  const upcomingRateRiseDateFormatted = upcomingRateRiseDate?.date
    ? dayjs(upcomingRateRiseDate.date).format('D MMM YYYY')
    : null

  // Check if there's no premium difference for upcoming rate rise
  const hasNoPremiumDifferenceForUpcomingRateRise = parseInt(product?.rate_rise?.upcoming_rate_rise)?.toFixed(2) === parseInt(product?.payload?.premium_amount || product?.data?.premium_amount)?.toFixed(2)

  // Check if there's no premium difference for existing rate rise
  const hasNoPremiumDifferenceForExistingRateRise = parseInt(product?.rate_rise?.new_premium)?.toFixed(2) === parseInt(product?.payload?.premium_amount || product?.data?.premium_amount)?.toFixed(2)

  // Calculate upcoming rate rise value
  const upcomingRateRiseValue = product?.rate_rise?.upcoming_rate_rise &&
    !hasNoPremiumDifferenceForUpcomingRateRise &&
    upcomingRateRiseDateFormatted
    ? formatCurrency(product?.rate_rise?.upcoming_rate_rise?.toFixed(2)?.replace(/\.00$/, ''))
    : null

  // Get current premium from rate rise
  const currentPremiumFromRateRise = product?.rate_rise?.current_premium

  // Check if product has upcoming rate rise
  const hasUpcomingRateRise = product?.is_purchase_history &&
    product?.vertical === memberhubConstants.VERTICAL_ID.HEALTH_INSURANCE &&
    !!upcomingRateRiseValue

  // Check if product has existing rate rise
  const hasExistingRateRise = product?.is_purchase_history &&
    product?.vertical === memberhubConstants.VERTICAL_ID.HEALTH_INSURANCE &&
    !currentPremiumFromRateRise &&
    product?.rate_rise?.new_premium &&
    !hasNoPremiumDifferenceForExistingRateRise
  const existingRateRiseValue = hasExistingRateRise?formatCurrency(product?.rate_rise?.new_premium):null

  // Members may only restate the premium on policies they bought through Compare Club
  const canUpdatePremium = !!product?.is_purchase_history &&
    [
      memberhubConstants.VERTICAL_ID.HEALTH_INSURANCE,
      memberhubConstants.VERTICAL_ID.ENERGY,
      memberhubConstants.VERTICAL_ID.LIFE_INSURANCE
    ].includes(product?.vertical)

  const hasUpdatedPremium = !!currentPremiumFromRateRise

  // Rate rises land on a fixed date each year but the new amount is not always known,
  // so once that date has passed we ask the member to confirm it themselves.
  const rateRiseDate = upcomingRateRiseDate?.date ? dayjs(upcomingRateRiseDate.date, 'YYYY-MM-DD') : null
  const rateRiseDatePassed = !!rateRiseDate?.isValid() && rateRiseDate.isSameOrBefore(dayjs(), 'day')
  const purchaseDate = product?.payload?.purchase_date ? dayjs(product.payload.purchase_date, 'DD/MM/YYYY') : null
  const purchasedBeforeRateRise = !purchaseDate?.isValid() || purchaseDate.isBefore(rateRiseDate, 'day')
  const needsPremiumConfirmation = canUpdatePremium &&
    rateRiseDatePassed &&
    purchasedBeforeRateRise &&
    !hasExistingRateRise &&
    !hasUpdatedPremium

  const userStore = useUserStore()
  return userStore.isEmailVerified?{
    upcomingRateRiseDate,
    upcomingRateRiseDateFormatted,
    hasNoPremiumDifferenceForUpcomingRateRise,
    hasNoPremiumDifferenceForExistingRateRise,
    upcomingRateRiseValue,
    currentPremiumFromRateRise,
    hasUpcomingRateRise,
    hasExistingRateRise,
    existingRateRiseValue,
    canUpdatePremium,
    hasUpdatedPremium,
    needsPremiumConfirmation
  }:{}
}

// Route slug the vault preview / step pages expect for a vertical id.
// Bill forwarding currently covers energy + health insurance. Resolved lazily
// (inside the fn) so this module doesn't read memberhubConstants at import time.
const forwardedBillVerticalSlug = vertical => {
  const slugs = {
    [memberhubConstants.VERTICAL_ID.ENERGY]: 'energy',
    [memberhubConstants.VERTICAL_ID.HEALTH_INSURANCE]: 'health-insurance'
  }
  return slugs[vertical] || vertical || ''
}

/*
 * Whether a vault product was added by forwarding a bill to the inbound email
 * address (vault `source === 'email_forward'`). Shared across product cards and
 * product-details pages via the getters below.
 * @param {Object} product - raw vault record
 * @returns {boolean}
 */
const isEmailForwardProduct = product =>
  product?.source === memberhubConstants.VAULT_SOURCE.EMAIL_FORWARD

/*
 * Whether a vault product was added from a bill found by scanning the member's
 * connected Gmail inbox (vault `source === VAULT_SOURCE.EMAIL_SCRAPE`).
 * @param {Object} product - raw vault record
 * @returns {boolean}
 */
const isEmailScrapeProduct = product =>
  product?.source === memberhubConstants.VAULT_SOURCE.EMAIL_SCRAPE

/*
 * Whether a vault product was auto-created from Energy CDR (Open Energy / Consumer
 * Data Right) consent data, rather than a manually-entered or file-upload product
 * (vault `source === VAULT_SOURCE.CDR_CONSENT`, set by CdrDataVaultCreator in
 * memberhub-backend) — same `source`-column pattern as the email-forward/email-scrape
 * getters above, rather than a CDR-only `extras.completion_source` special case.
 * @param {Object} product - raw vault record
 * @returns {boolean}
 */
const isCdrConnectedProduct = product =>
  product?.source === memberhubConstants.VAULT_SOURCE.CDR_CONSENT

/*
 * Display status for a flattened forwarded bill:
 *  - failed parse (no usable bill)  → draft_failed
 *  - draft awaiting member review    → draft_review
 *  - confirmed / created vault       → vault_created
 * @param {Object} bill - raw flattened bill
 * @returns {string} a FORWARDED_BILL_STATUS value
 */
const forwardedBillStatus = bill => {
  const failed =
    bill?.parsed_status === memberhubConstants.FILE_PARSED_STATUS.FAILED ||
    bill?.status === memberhubConstants.FORWARDED_BILL_STATUS.DRAFT_FAILED ||
    bill?.status === 'failed'
  if (failed) return memberhubConstants.FORWARDED_BILL_STATUS.DRAFT_FAILED
  return bill?.is_draft
    ? memberhubConstants.FORWARDED_BILL_STATUS.DRAFT_REVIEW
    : memberhubConstants.FORWARDED_BILL_STATUS.VAULT_CREATED
}

/*
 * Display service address for a bill's `details.address` (drafts carry it):
 * the API's address line, else suburb/state/postcode. Accepts a plain string.
 * @param {Object|string} address
 * @returns {string}
 */
const billServiceAddress = address => {
  if (!address) return ''
  if (typeof address === 'string') return address
  return address.addressline || [address.suburb, address.state, address.postcode].filter(Boolean).join(' ')
}

/*
 * Tiles for an email-bills list (forwarded or imported): normalised products
 * (from bills) plus a failed tile for every attachment whose parse failed
 * (from files).
 * @param {Array} bills - flattened bills (see flattenBillEmails in actions)
 * @param {Array} files - flattened files
 * @returns {*[]}
 */
const emailBillTiles = (bills, files) => {
  const productTiles = (bills || []).map(bill => {
    const isDraft = !!bill.is_draft
    // created = the forward produced this vault; merged = it was folded into a
    // vault the member already had (CLUB-800).
    const outcome = bill.outcome || 'created'
    return {
      // Unique per email + vault: a merged vault is listed under every email
      // that fed it, so the vault uuid alone repeats across the flattened list.
      // Imported drafts carry no email, so theirs is the vault alone.
      key: [bill.email_id, bill.uuid ?? bill.vault_id].filter(part => part != null).join(':'),
      vaultId: bill.vault_id,
      uuid: bill.uuid,
      vertical: bill.vertical,
      verticalLabel: memberhubConstants.VERTICAL_LABELS[bill.vertical] || bill.vertical || '',
      // Route slug for opening the preview/review screen (falls back to a
      // string vertical if the API already sends a slug).
      verticalSlug: forwardedBillVerticalSlug(bill.vertical),
      isDraft,
      outcome,
      isMerged: outcome === 'merged',
      matchConfidence: bill.match_confidence,
      emailId: bill.email_id,
      emailStatus: bill.email_status,
      // draft_failed (parse failed) | draft_review (needs review) | vault_created (added)
      status: forwardedBillStatus(bill),
      // Display fields from the bill's `details` block:
      //  energy → { retailer, product_type, nickname, logo_url }
      //  health → { provider, name, cover_type, logo_url }
      provider: bill.details?.retailer || bill.details?.provider || bill.provider || bill.retailer || '',
      title: bill.details?.nickname || bill.details?.name || bill.details?.product_type
        || bill.name || bill.product_name || bill.title || memberhubConstants.VERTICAL_LABELS[bill.vertical] || '',
      logoUrl: bill.details?.logo_url || '',
      coverType: bill.details?.cover_type || '',
      productType: bill.details?.product_type || '',
      serviceAddress: billServiceAddress(bill.details?.address),
      // Original attachment name — shown on failed cards where there is no
      // parsed product to display.
      fileName: bill.file_name || bill.filename || bill.name || ''
    }
  })

  // Failed attachments become failed tiles (vertical unknown for now).
  const failedFileTiles = (files || [])
    .filter(file => file.parsed_status === memberhubConstants.FILE_PARSED_STATUS.FAILED)
    .map(file => ({
      key: `${file.email_id}:file-${file.id ?? file.name}`,
      fileId: file.id,
      uuid: file.uuid || `file-${file.id ?? file.name}`,
      vertical: 'unknown',
      verticalLabel: 'Unknown',
      verticalSlug: '',
      isDraft: false,
      emailId: file.email_id,
      emailStatus: file.email_status,
      status: memberhubConstants.FORWARDED_BILL_STATUS.DRAFT_FAILED,
      provider: '',
      title: file.name || '',
      fileName: file.name || '',
      isFailedFile: true
    }))

  return [...productTiles, ...failedFileTiles]
}

/*
 * Count of bills still being processed — emails not yet finalised
 * (received / awaiting_n8n), which have no products to show as cards yet.
 * Sums each such email's attachment_count (fallback 1) so the counter reflects
 * the number of bills in flight, not just the number of emails.
 * @param {Array} emails - raw email objects
 * @returns {number}
 */
const emailBillsProcessingCount = emails => {
  const processingStatuses = [
    memberhubConstants.FORWARDED_BILL_STATUS.RECEIVED,
    memberhubConstants.FORWARDED_BILL_STATUS.AWAITING_N8N
  ]
  return (emails || [])
    .filter(email => processingStatuses.includes(email.status))
    .reduce((total, email) => total + (parseInt(email.attachment_count, 10) || 1), 0)
}

// The API sends amounts and usage as decimal strings ("102.00", "362.000").
const toNumberOrNull = value => {
  if (value === null || value === undefined || value === '') return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

/*
 * Display shape for a product's energy bill history: newest first by billed
 * date, with only the most recent bill marked latest. List fields come from
 * GET /vaults/{vault}/bills; the detail fields (billing period, next payment,
 * usage, source document) from GET /vaults/{vault}/bills/{bill} once that
 * bill's detail is fetched, and stay empty until then (isDetailLoaded false).
 * Shapes: tests/tests/fixtures/energyBillHistory.fixture.ts.
 * @param {Array} bills - raw bill-history list items
 * @param {Object} [details] - raw bill details for this product, by bill id
 * @returns {*[]}
 */
const energyBillHistoryItems = (bills, details = {}) => {
  return [...(bills || [])]
    .sort((a, b) => String(b.billed_date || '').localeCompare(String(a.billed_date || '')))
    .map((bill, index) => {
      const detail = details?.[bill.id]
      const document = detail?.source_document
      return {
        id: bill.id,
        billedDate: detail?.billed_date || bill.billed_date,
        amount: toNumberOrNull(detail?.amount ?? bill.amount),
        amountPeriod: detail?.amount_period ?? bill.amount_period ?? null,
        // GST within the amount (CDR bill detail only)
        gstAmount: toNumberOrNull(detail?.gst_amount),
        isLatest: index === 0,
        // A CDR bill (CLUB-897): bill details shows all its fields, latest included
        isCdr: !!bill.is_cdr,
        billingPeriod: detail?.billing_period
          ? { start: detail.billing_period.start, end: detail.billing_period.end, label: detail.billing_period.label ?? null }
          : null,
        nextPaymentDate: detail?.next_payment_date ?? null,
        usage: toNumberOrNull(detail?.usage?.value),
        usageUnit: detail?.usage?.unit ?? null,
        document: document
          ? { name: document.file_name, size: document.size_bytes, url: document.file_url, caption: document.caption ?? '' }
          : null,
        // CDR bill detail only
        paymentStatus: detail?.payment_status ?? null,
        usageRate: toNumberOrNull(detail?.usage_rate),
        discountAmount: toNumberOrNull(detail?.discount_amount),
        isDetailLoaded: !!detail
      }
    })
}

const getters = {
  /*
     * Accessor for user vault formData
     * @param state
     * @returns {*|{}}
     */
  getVaultFormData: state => {
    return state.vaultFormData || {}
  },
  /*
     * Accessor for vaults products formatted
     * @param state
     * @param getters
     * @returns {*}
     */
  getVaultProductsFormatted (state) {
    /*
          | =================================================
          | @param product
          | @returns {[{description: string, title: string}]}
          | =================================================
          */
    const productDetailsPerVertical = product => {
      const extraCoverExists = product?.payload?.policy_details && product?.payload?.policy_details.product_type === 'Hospital' && product?.payload?.cover_type === 'Combined'
      switch (product.vertical) {
        case memberhubConstants.VERTICAL_ID.HEALTH_INSURANCE:
          if (product.is_purchase_history) {
            return [
              {
                title: 'Policy name',
                description: `${product.payload?.policy_details?.derived_name || product.payload?.policy_details?.name || ''}${(extraCoverExists ? ` with ${product.payload?.extra_details?.derived_name || product.payload?.extra_details?.name}` : '')}`
              },
              {
                title: 'Coverage',
                description: product.payload?.coverage === 'Single Parent' ? 'Single Parent Family' : (product.payload?.coverage || '')
              },
              {
                title: 'Excess',
                description: product.payload?.policy_details?.excess ? '$' + product.payload?.policy_details?.excess : ''
              },
              {
                title: 'Membership #',
                description: product.payload?.policy_details?.new_membership_number || ''
              },
              {
                title: 'Application Date',
                description: product.payload?.purchase_date || ''
              },
              {
                title: 'Compare Club reference #',
                description: product?.opportunity_id || ''
              }
            ]
          }

          return [
            {
              title: 'Policy name',
              description: `${product.payload?.policy_details?.derived_name || product.payload?.policy_details?.name || ''}${(extraCoverExists ? ` with ${product.payload?.extra_details?.derived_name || product.payload?.extra_details?.name}` : '')}`
            },
            {
              title: 'Reference #',
              description: product.payload?.reference || ''
            },
            {
              title: 'Next payment',
              description: product.payload?.actual_next_payment_date || ''
            },
            {
              title: 'Coverage',
              description: product.payload?.coverage === 'Single Parent' ? 'Single Parent Family' : (product.payload?.coverage || '')
            },
            {
              title: 'Comments',
              description: product.payload?.comments || ''
            }
          ]
        case memberhubConstants.VERTICAL_ID.ENERGY:
          if (product.is_purchase_history) {
            const details = [
              {
                title: 'Retailer',
                description: product.payload?.retailer || ''
              },
              {
                title: 'Product Type',
                description: strToCapitalize(getKey(product.payload?.energy_product_type, memberhubConstants.ENERGY_PRODUCT_TYPE)?.toLowerCase()) || ''
              },
              {
                title: 'Plan',
                description: product.payload?.plan || ''
              },
              {
                title: 'Application Date',
                description: product.payload?.application_date ? dayjs(product.payload?.application_date, 'DD/MM/YYYY').format('DD MMM YYYY') : ''
              },
              {
                title: 'Address',
                description: product.payload?.address?.addressline || product.payload?.address || ''
              }
            ]
            if (product.payload?.energy_product_type === memberhubConstants.ENERGY_PRODUCT_TYPE_FLAG.ELECTRICITY) {
              details.push(...[
                {
                  title: 'NMI Number',
                  description: product.payload?.nmi_number || ''
                }
              ])
            }
            if (product.payload?.energy_product_type === memberhubConstants.ENERGY_PRODUCT_TYPE_FLAG.GAS) {
              details.push(...[
                {
                  title: 'MIRN Number',
                  description: product.payload?.mirn_number || ''
                }
              ])
            }
            details.push({
              title: 'Compare Club Reference #',
              description: product.payload?.live_submit_id || ''
            })
            return details
          }
          return [
            {
              title: 'Product',
              description: `${product.payload?.product_type} with ${product.payload?.retailer?.fund_name}`
            },
            {
              title: 'Next payment',
              description: product.payload?.actual_next_payment_date || ''
            },
            {
              title: 'Address',
              description: product.payload?.address?.addressline || product.payload?.address || ''
            },
            {
              title: 'Typical usage',
              description: product.payload?.energy_usage || ''
            },
            {
              title: 'Comments',
              description: product.payload?.comments || ''
            }
          ]
        case memberhubConstants.VERTICAL_ID.HOME_LOANS:
          // eslint-disable-next-line no-case-declarations
          const loanTerm = product.payload?.loan_term !== 'Other' ? product.payload?.loan_term?.replace(' year', '') : null
          // eslint-disable-next-line no-case-declarations
          const duration = loanTerm ? renderDuration(dayjs(), dayjs(product.payload?.loan_start_date, 'DD/MM/YYYY').add(loanTerm, 'year')) : null
          // eslint-disable-next-line no-case-declarations
          const remainingTerm = duration ? (duration.years ? `${duration.years} years` : '') + (duration.months ? ` ${duration.months} months` : '') : null
          return [
            {
              title: 'Lender',
              description: product.payload?.lender?.fund_name || ''
            },
            {
              title: 'Balance**',
              description: product.payload?.current_balance ? `${formatCurrency(product.payload?.current_balance)} ${(remainingTerm ? `over the next ${remainingTerm}` : '')}` : ''
            },
            {
              title: 'Loan type',
              description: product.payload?.loan_type || ''
            },
            {
              title: `${product.payload?.loan_type === 'Split (Combined)' ? 'Rates' : 'Rate'}`,
              description: productInterestRateString(product)
            },
            {
              title: 'Loan amount',
              description: homeLoanAmount(product)
            },
            {
              title: 'Started on',
              description: product.payload?.loan_start_date || ''
            },
            {
              title: 'Payment type',
              description: product.payload?.payment_type || ''
            },
            {
              title: 'Payment frequency',
              description: product.payload?.payment_frequency || ''
            },
            {
              title: 'Property type',
              description: product.payload?.property_type || ''
            },
            {
              title: 'Comments',
              description: product.payload?.comments || ''
            }
          ]
        case memberhubConstants.VERTICAL_ID.LIFE_INSURANCE:
          if (product.is_purchase_history) {
            const details = [
              {
                title: 'Provider',
                description: product.payload?.proceeding_insurer || ''
              },
              {
                title: 'Policy coverage',
                grouped: product.payload?.in_force_date ? dayjs(product.payload?.in_force_date, 'YYYY-MM-DD').isAfter(dayjs()) : true,
                policyCoverageTooltip: product.payload?.in_force_date ? !dayjs(product.payload?.in_force_date, 'YYYY-MM-DD').isAfter(dayjs()) : false,
                items: !product.payload?.in_force_date || dayjs(product.payload?.in_force_date, 'YYYY-MM-DD').isAfter(dayjs())
                  ? [
                    {
                      label: 'Life cover',
                      value: product.payload?.life_cover_quoted && parseFloat(product.payload?.life_cover_quoted) > 0 ? formatCurrency(formatAmount(product.payload?.life_cover_quoted)) || '' : ''
                    }, {
                      label: 'TPD cover',
                      value: product.payload?.tpd_quoted && parseFloat(product.payload?.tpd_quoted) > 0 ? formatCurrency(formatAmount(product.payload?.tpd_quoted)) || '' : ''
                    }, {
                      label: 'Trauma cover',
                      value: product.payload?.trauma_quoted && parseFloat(product.payload?.trauma_quoted) > 0 ? formatCurrency(formatAmount(product.payload?.trauma_quoted)) || '' : ''
                    }, {
                      label: 'Income protection',
                      value: product.payload?.income_protection && parseFloat(product.payload?.income_protection) > 0 ? (formatCurrency(formatAmount(product.payload?.income_protection)) || '') : ''
                    }
                  ]
                  : []
              }
            ]
            if (dayjs().diff(dayjs(product.payload?.in_force_date, 'YYYY-MM-DD'), 'day') >= 0 || !['Submitted', 'Assessed'].includes(product.payload?.status)) {
              details.push(...[
                {
                  title: 'Payment method',
                  description: product.payload?.payment_method || ''
                },
                {
                  title: 'Start date',
                  description: product.payload?.in_force_date ? dayjs(product.payload?.in_force_date, 'YYYY-MM-DD').format('DD/MM/YYYY') || '' : ''
                },
                {
                  title: 'Policy #',
                  description: product.payload?.policy_number || ''
                }
              ])
            } else {
              details.push(...[
                {
                  title: 'Price',
                  description: formatCurrency(product.payload?.premium) || ''
                },
                {
                  title: 'Payment frequency',
                  description: product.payload?.payment_frequency || ''
                },
                (['Submitted', 'Assessed'].includes(product.payload?.status)
                  ? {
                    title: 'Payment method',
                    description: product.payload?.payment_method || ''
                  }
                  : {}),
                {
                  title: 'Expected start date',
                  description: product.payload?.expected_inforce_date ? dayjs(product.payload?.expected_inforce_date).format('DD/MM/YYYY') || '' : ''
                },
                {
                  title: 'Application date',
                  description: product.payload?.app_submit_date ? dayjs(product.payload?.app_submit_date).format('DD/MM/YYYY') || '' : ''
                }
              ])
            }
            details.push(...[
              {
                title: 'Compare Club Reference #',
                description: product.payload?.live_submit_id || ''
              }
            ])
            return details
          }
          return [
            {
              title: 'Product',
              description: product.payload?.provider?.fund_name || ''
            },
            {
              title: 'Reference #',
              description: product.payload?.reference || ''
            },
            {
              title: 'Next payment',
              description: product.payload?.actual_next_payment_date || ''
            },
            {
              title: 'Payment Method',
              description: product.payload?.payment_method || ''
            },
            {
              title: 'Sum insured',
              grouped: true,
              items: [
                {
                  label: 'Life cover',
                  value: product.payload?.life_cover ? formatCurrency(product.payload?.life_cover) : null
                }, {
                  label: 'TPD cover',
                  value: product.payload?.tpd_cover ? formatCurrency(product.payload?.tpd_cover) : null
                }, {
                  label: 'Trauma cover',
                  value: product.payload?.trauma_cover ? formatCurrency(product.payload?.trauma_cover) : null
                }, {
                  label: 'Income protection',
                  value: product.payload?.income_protection ? formatCurrency(product.payload?.income_protection) : null
                }
              ]
            },
            {
              title: 'Comments',
              description: product.payload?.comments || ''
            }
          ]
        case memberhubConstants.VERTICAL_ID.CAR_INSURANCE:
        case memberhubConstants.VERTICAL_ID.GENERIC:
          if (product.is_purchase_history) {
            const details = [
              {
                title: 'Retailer',
                description: product.payload?.retailer || ''
              },
              {
                title: 'Product Type',
                description: strToCapitalize(getKey(product.payload?.generic_product_type, memberhubConstants.GENERIC_PRODUCT_TYPE)?.toLowerCase()) || ''
              },
              {
                title: 'Plan',
                description: product.payload?.plan || ''
              },
              {
                title: 'Application Date',
                description: product.payload?.application_date ? dayjs(product.payload?.application_date, 'DD/MM/YYYY').format('DD MMM YYYY') : ''
              }
            ]
            if (product.payload?.generic_product_type === memberhubConstants.GENERIC_PRODUCT_TYPE_FLAG.BROADBAND) {
              details.push(...[
                {
                  title: 'Address',
                  description: product.payload?.address || ''
                }
              ])
            }
            details.push(...[
              {
                title: 'Telco Reference #',
                description: product.payload?.telco_ref_number || ''
              },
              {
                title: 'Compare Club Reference #',
                description: product.payload?.live_submit_id || ''
              }
            ])
            return details
          }
          return [
            {
              title: 'Provider',
              description: product.payload?.provider || ''
            },
            {
              title: 'Reference #',
              description: product.payload?.reference || ''
            },
            {
              title: 'Next payment',
              description: product.payload?.actual_next_payment_date || ''
            },
            {
              title: 'Renewal date',
              description: product.payload?.renewal_date || ''
            },
            {
              title: 'Comments',
              description: product.payload?.comments || ''
            }
          ]
        default:
          return [
            {
              title: 'Policy name',
              description: `${product.payload?.policy_details?.derived_name || product.payload?.policy_details?.name}${(extraCoverExists ? ` with ${product.payload?.extra_details?.derived_name || product.payload?.extra_details?.name}` : '')}`
            },
            {
              title: 'Reference #',
              description: product.payload?.reference || ''
            },
            {
              title: 'Next payment',
              description: product.payload?.actual_next_payment_date || ''
            },
            {
              title: 'Coverage',
              description: product.payload?.coverage === 'Single Parent' ? 'Single Parent Family' : (product.payload?.coverage || '')
            },
            {
              title: 'Comments',
              description: product.payload?.comments || ''
            }
          ]
      }
    }
    return state.products?.map(product => {
      return {
        id: product.id,
        uuid: product.uuid,
        active: product.active,
        vertical: product.vertical,
        is_purchase_history: product.is_purchase_history,
        purchase_date: product?.payload?.purchase_date,
        name: product.payload?.nickname || productNamePerVertical(product),
        premium_amount: premiumAmountPerVertical(product),
        payment_frequency: paymentFrequencyPerVertical(product),
        productDetails: [...productDetailsPerVertical(product).filter(p => memberhubConstants.INLINE_EDIT_FIELDS.filter(f => product.vertical === memberhubConstants.VERTICAL_ID.GENERIC?f.label !== 'Next payment':true).find(f => f.label === p.title)||!!p.description || !!p.grouped || !!p.policyCoverageTooltip), ...[{
          title: 'Policy Document',
          files: product.files || []
        }]],
        files: product.files || [],
        category: product?.payload?.category === 'Other' ? product?.payload?.other_category : product?.payload?.category,
        income_tier_rebate: product?.payload?.income_tier_rebate,
        data: product?.payload,
        rate_rise: product?.rate_rise,
        live_submit_id: product?.payload?.live_submit_id,
        status: licStatus(product?.payload?.status)
      }
    }) || []
  },
  /*
     * Accessor for vaults products simplified formatted for v2 design
     * @param state
     * @returns {*[]}
     */
  getProducts (state){
    return state.products?.map(product => {
      // Generate CTA buttons for this product
      const ctaButtons = []
      const isHealth = product.vertical === memberhubConstants.VERTICAL_ID.HEALTH_INSURANCE
      const isGeneric = product.vertical === memberhubConstants.VERTICAL_ID.GENERIC
      const isEnergy = product.vertical === memberhubConstants.VERTICAL_ID.ENERGY
      const isOpenApplication = checkLifePurchase(product) && !checkActiveLifePurchase(product)
      
      /*
       * The primary CTA is withheld for 30 days after purchase, so a member who
       * has just bought is not immediately invited to compare again.
       *
       * The purchase date arrives under a different key and format per vertical,
       * so resolve the source rather than reading one fixed key:
       *   health — payload.purchase_date, either DD/MM/YYYY or YYYY-MM-DD
       *   energy — payload.application_date, strictly DD/MM/YYYY (CLUB-756), and
       *            purchases only, since that key comes from the Salesforce sync
       * Verticals absent here have no cooldown and keep their CTA immediately.
       *
       * Reading purchase_date for energy would make the gate a silent no-op:
       * energy payloads never carry that key.
       */
      const ctaCooldownSource = isHealth
        ? { value: product?.payload?.purchase_date, formats: ['DD/MM/YYYY', 'YYYY-MM-DD'] }
        : (isEnergy && product?.is_purchase_history
          ? { value: product?.payload?.application_date, formats: ['DD/MM/YYYY'] }
          : null)

      const ctaCooldownDate = ctaCooldownSource?.value
        ? dayjs(ctaCooldownSource.value, ctaCooldownSource.formats, true)
        : null

      // Absent or unparseable dates fail open — there is no cooldown to apply.
      const hasValidPurchaseDate = !!ctaCooldownDate?.isValid()

      const purchaseBeyondCase = hasValidPurchaseDate &&
        ctaCooldownDate.isBefore(dayjs().startOf('day').subtract(30, 'day'), 'day')
      
      // Determine if comparison is available
      const hasComparison = isHealth
      
      // Determine if product can view details
      const canViewDetails = [
        memberhubConstants.VERTICAL_ID.HEALTH_INSURANCE,
        memberhubConstants.VERTICAL_ID.HOME_LOANS,
        memberhubConstants.VERTICAL_ID.LIFE_INSURANCE,
        memberhubConstants.VERTICAL_ID.ENERGY
      ].includes(product.vertical)
      
      // Determine if should show primary CTA
      const shouldShowPrimaryCta = !isGeneric && (!ctaCooldownSource || !hasValidPurchaseDate || purchaseBeyondCase) &&
        !checkLifeQuickCompareLocked(product)
      
      // Get redirect path based on vertical
      const getRedirectPath = vertical => {
        const redirectPaths = {
          [memberhubConstants.VERTICAL_ID.HEALTH_INSURANCE]: '/health-insurance/quote/form/step1/',
          [memberhubConstants.VERTICAL_ID.HOME_LOANS]: memberhubConstants.HOME_LOAN_QUICK_COMPARE_PATH,
          [memberhubConstants.VERTICAL_ID.LIFE_INSURANCE]: memberhubConstants.LIFE_QUICK_COMPARE_PATH,
          [memberhubConstants.VERTICAL_ID.ENERGY]: memberhubConstants.ENERGY_QUICK_COMPARE_PATH,
          [memberhubConstants.VERTICAL_ID.CAR_LOANS]: '/personal-loans/quote/form/step1/',
          [memberhubConstants.VERTICAL_ID.PERSONAL_LOANS]: '/personal-loans/quote/form/step1/',
          [memberhubConstants.VERTICAL_ID.CAR_INSURANCE]: '/car-insurance/',
          [memberhubConstants.VERTICAL_ID.CREDIT_CARDS]: '/credit-cards/all/'
        }
        return redirectPaths[vertical] || null
      }

      if (shouldShowPrimaryCta) {
        let label = product.vertical === memberhubConstants.VERTICAL_ID.ENERGY ? 'Quick Compare' : 'Get a Quote'
        let action = 'redirect'
        let redirectPath = getRedirectPath(product.vertical)
        const sourceQueryAllowedVerticals = [memberhubConstants.VERTICAL_ID.HEALTH_INSURANCE, memberhubConstants.VERTICAL_ID.LIFE_INSURANCE]

        if (hasComparison) {
          label = 'One-Click Compare'
          action = 'comparison'
          if (redirectPath && sourceQueryAllowedVerticals.includes(product.vertical)) {
            redirectPath = `${redirectPath}?source=clubplus-dashboard`
          }
        } else if (isOpenApplication) {
          label = 'Contact Your Broker'
          action = 'phone'
        } else if (isEnergy) {
          label = 'Quick Compare'
          if (product.comparison_lead_uuid) {
            redirectPath = `/energy/quotes-results/?uuid=${product.comparison_lead_uuid}&source=clubplus-dashboard`
          } else {
            redirectPath = `/dashboard/quick-compare/energy/${product.uuid}`
          }
        }

        ctaButtons.push({
          id: `vault-primary-cta-${product.vertical}`,
          type: 'primary',
          theme: 'primary',
          size: 'large',
          full: true,
          text: label,
          showSuperscriptAsterisk: hasComparison,
          action,
          redirectPath,
          class: 'sm:whitespace-nowrap mb-auto',
          trackingParams: {
            clubplus_enquiry: true,
            clubplus_enquiry_origin: 'comparemypolicy_online',
            vertical: product.vertical
          }
        })
      }

      // View Details Button
      if (canViewDetails) {
        ctaButtons.push({
          id: `view-more-details-cta-${product.vertical}`,
          type: 'secondary',
          theme: 'tertiary',
          size: 'large',
          full: true,
          text: 'View More Details',
          action: 'viewDetails',
          class: 'mt-auto !h-10',
          trackingParams: {}
        })
      }

      const details = productAdditionalDetails(product) || []
      const genericAllowedTitles = ['Next Payment Date', 'Renewal/Expiry Date']
      const additionalDetails = isGeneric
        ? details.filter(detail => !!detail.description || genericAllowedTitles.includes(detail.title))
        : details.filter(detail => !!detail.description)
      return {
        id: product.id,
        uuid: product.uuid,
        vertical: product.vertical,
        vertical_label: memberhubConstants.VERTICAL_LABELS[product.vertical] || 'Unknown',
        is_purchase_history: product.is_purchase_history,
        is_email_forward: isEmailForwardProduct(product),
        is_email_scrape: isEmailScrapeProduct(product),
        is_cdr_connected: isCdrConnectedProduct(product),
        name: product.payload?.nickname || productNamePerVertical(product),
        premium_amount: premiumAmountPerVertical(product),
        payment_frequency: paymentFrequencyPerVertical(product),
        category: (product?.payload?.category === 'Other' ? product?.payload?.other_category : product?.payload?.category) || (product?.payload?.generic_product_type?'TELCO':''),
        income_tier_rebate: product?.payload?.income_tier_rebate,
        provider_name: providerNamePerVertical(product),
        status: licStatus(product?.payload?.status),
        is_open_application: isOpenApplication,
        latest_score: product.latest_score,
        additional_details: additionalDetails,
        created_at: product?.created_at,
        canBeReviewed: product?.payload?.policy_start_date? dayjs(product?.payload?.policy_start_date,'YYYY-MM-DD').isSameOrBefore(dayjs()):true,
        purchase_date: product?.payload?.purchase_date || product?.payload?.close_date,
        date_added: dateAddedToVault(product),
        ctaButtons,
        payload: product.payload,
        rateRiseConfig: getRateRiseConfig(product),
        health_policy_type: getHealthPolicyType(product),
        review_status_locked: product.review_status_locked,
        review_status_opted_in_at: product.review_status_opted_in_at,
        review_status_unlock_eligible: product.review_status_unlock_eligible,
        show_review_status: product.show_review_status
      }
    }) || []
  },
  /*
   * Whether the product detail must be refetched before use: a different vault, never
   * fetched, or older than VAULT_PRODUCT_MAX_AGE_MS. Its presigned file URLs expire after
   * 15 minutes (CLUB-899), so a cached detail cannot be reused indefinitely.
   * @param state
   * @returns {function(string, number=): boolean}
   */
  isProductStale: state => (uuid, now = Date.now()) => {
    return !state.product?.uuid ||
      state.product.uuid !== uuid ||
      !state.product_fetched_at ||
      now - state.product_fetched_at > memberhubConstants.VAULT_PRODUCT_MAX_AGE_MS
  },
  getProduct (state) {
    if(state.product?.uuid){
      if(state.product?.payload?.policy_start_date){
        state.product.canBeReviewed = dayjs(state.product.payload.policy_start_date,'YYYY-MM-DD').isSameOrBefore(dayjs())
      }else{
        state.product.canBeReviewed = true
      }
      state.product.payment_frequency = paymentFrequencyPerVertical(state.product)
      state.product.date_added = dateAddedToVault(state.product)
      if(checkLifePurchase(state.product)){
        state.product.is_open_application = !checkActiveLifePurchase(state.product)
      }
      state.product.rateRiseConfig = getRateRiseConfig(state.product)
      state.product.health_policy_type = getHealthPolicyType(state.product)
      state.product.is_email_forward = isEmailForwardProduct(state.product)
      state.product.is_email_scrape = isEmailScrapeProduct(state.product)
      state.product.is_cdr_connected = isCdrConnectedProduct(state.product)
    }
    return state.product
  },
  getLifeProduct (state) {
    return state.lifeProduct
  },
  getVaultArchivedProductsFormatted (state) {
    return state.archivedProducts?.map(product => {
      return {
        id: product.id,
        uuid: product.uuid,
        vertical: product.vertical,
        vertical_label: memberhubConstants.VERTICAL_LABELS[product.vertical] || 'Unknown',
        is_purchase_history: product.is_purchase_history,
        purchase_date: product?.payload?.purchase_date,
        name: product.payload?.nickname || productNamePerVertical(product),
        premium_amount: premiumAmountPerVertical(product),
        payment_frequency: paymentFrequencyPerVertical(product),
        category: product?.payload?.category === 'Other' ? product?.payload?.other_category : product?.payload?.category,
        data: product?.payload,
        rate_rise: product?.rate_rise,
        archived_on: dayjs(product?.extras?.archived_at).format('DD/MM/YYYY')
      }
    }) || []
  },
  /*
 * Retrieves the slugs of purchased verticals' perks categories.
 *
 * @param {Object} state - The state object containing products.
 * @returns {Array} - An array of slugs for the purchased verticals' perks categories.
 */
  getPurchasedVerticalsPerksCategorySlugs (state) {
    const constraintPeriod = vertical => state.verticals?.find(v => parseInt(v.id) === parseInt(vertical))?.config?.constraint_period || 24
    return Object.keys(memberhubConstants.PERKS_CATEGORY_SLUGS)
      .filter(slug => state.products.some(product => {
        return product.is_purchase_history && dayjs().diff(getPurchaseDate(product), 'month') <= constraintPeriod(product.vertical) && product.vertical === parseInt(slug)
      }))
      .map(slug => memberhubConstants.PERKS_CATEGORY_SLUGS[slug])
  },

  getProductByUUID (state) {
    return uuid => {
      return state.products.find(product => product.uuid === uuid)
    }
  },

  /**
   * Check if current product is a life insurance purchase
   * @param {Object} state
   * @returns {boolean}
   */
  isLifePurchase (state) {
    return checkLifePurchase(state.product)
  },

  /**
   * Check if life insurance purchase is active (in-force date has passed)
   * @param {Object} state
   * @returns {boolean}
   */
  isActiveLifePurchase (state) {
    return checkActiveLifePurchase(state.product)
  },

  /**
   * Check if current life insurance purchase is within the 90-day post-sale
   * Quick Compare lock window
   * @param {Object} state
   * @returns {boolean}
   */
  isLifeQuickCompareLocked (state) {
    return checkLifeQuickCompareLocked(state.product)
  },
  /* Accessor for smart bill failed files list
   * @param state
   * @returns {[]|*[]}
   */
  getSmartBillUploadFailedFiles (state) {
    return state.smartBillUpload?.failedFiles || []
  },
  getSmartBillUploadFailedFilesForVaultForm (state) {
    return this.getSmartBillUploadFailedFiles?.map(file => ({
      type:memberhubConstants.UPLOADER_WIDGET.FILE_TYPES.FILE,
      file_id: file.uploadedObject.id,
      file_url: null,
      name: file.name
    }))
  },
  getSmartBillUploadedFiles (state) {
    return state.smartBillUpload?.uploadedFiles || []
  },
  getSmartBillStorageConsent (state) {
    return state.smartBillUpload?.storageConsent || false
  },
  /*
   * Accessor for the raw forwarded emails (one item per inbound email, each with
   * its own `bills` array). Kept separate for future email-level UI.
   * @param state
   * @returns {*[]}
   */
  getForwardedEmails (state) {
    return state.forwardedEmails || []
  },
  /*
   * Accessor for the raw flattened files (all attachments across every email).
   * Kept for future file-level UI.
   * @param state
   * @returns {*[]}
   */
  getForwardedFiles (state) {
    return state.forwardedFiles || []
  },
  /*
   * Tiles for the forwarded-bills list: normalised products (from bills) plus a
   * failed tile for every attachment whose parse failed (from files).
   * @param state
   * @returns {*[]}
   */
  getForwardedBills (state) {
    return emailBillTiles(state.forwardedBills, state.forwardedFiles)
  },
  /*
   * Count of bills still being processed — emails not yet finalised
   * (received / awaiting_n8n), which have no products to show as cards yet.
   * Sums each such email's attachment_count (fallback 1) so the counter reflects
   * the number of bills in flight, not just the number of emails.
   * @param state
   * @returns {number}
   */
  getForwardedBillsProcessingCount (state) {
    return emailBillsProcessingCount(state.forwardedEmails)
  },
  /*
   * Tiles for the imported-bills list: one review tile per draft vault, with
   * the same normalisation as getForwardedBills. Drafts come without
   * attachments, so there are no failed-file tiles.
   * @param state
   * @returns {*[]}
   */
  getImportedBills (state) {
    return emailBillTiles(state.importedBills, [])
  },
  /*
   * Accessor for a product's energy bill history, newest first (see
   * energyBillHistoryItems). Empty until fetchBillHistory has run for that
   * product, and empty when the fetch failed.
   * @param state
   * @returns {function(string): *[]}
   */
  getBillHistory (state) {
    return vaultId => energyBillHistoryItems(state.billHistory?.[vaultId], state.billDetails?.[vaultId])
  },
  /*
   * Whether a bill's detail request failed (bill details then stops loading and
   * shows the list fields).
   * @param state
   * @returns {function(string, (string|number)): boolean}
   */
  isBillDetailFailed (state) {
    return (vaultId, billId) => !!state.billDetailFailed?.[vaultId]?.[billId]
  },
  /*
   * The API's Bills History summary for a vault ({count, periodMonths}), or
   * null when it sent none (email history; the section derives it instead).
   * @param state
   * @returns {function(string): ({count: number|null, periodMonths: number|null}|null)}
   */
  getBillHistorySummary (state) {
    return vaultId => state.billHistorySummary?.[vaultId] || null
  },
  /*
   * A vault's raw CDR consent (consent_id, consent_created_at, ...), or null
   * when it has none or it isn't fetched yet.
   * @param state
   * @returns {function(string): (Object|null)}
   */
  getCdrConsent (state) {
    return vaultId => state.cdrConsent?.[vaultId] || null
  },
  /*
   * CDR consent state for a vault: undefined until fetched, then whether the
   * vault has a CDR consent (its bills come from CDR).
   * @param state
   * @returns {function(string): (boolean|undefined)}
   */
  isCdrBillsVault (state) {
    return vaultId => {
      if (!Object.prototype.hasOwnProperty.call(state.cdrConsent || {}, vaultId)) return undefined
      return !!state.cdrConsent[vaultId]
    }
  }
}

export default getters
export { getRateRiseConfig, isCdrConnectedProduct, isEmailForwardProduct, isEmailScrapeProduct, premiumAmountPerVertical, paymentFrequencyPerVertical, productNamePerVertical }
